/*
 * One spoken turn, one bubble.
 *
 * The caller's words reach a transcript more than once. The agent publishes
 * each final speech-to-text segment as it is recognised and then the whole
 * committed turn, which is those segments joined; the Avatar Panel also streams
 * its own copy of every segment. A caller who paused mid-sentence saw "Can
 * you", "search for Oxford shirt?" and then "Can you search for Oxford shirt?",
 * and a turn heard in one piece could show up twice.
 *
 * foldCallerTurn looks only at the caller bubbles since the agent last spoke,
 * so it never touches an earlier turn, and folds the bubble at `index` into
 * them when it repeats what is already there:
 *   - the same words as one of them: the copy merges into that bubble;
 *   - the words of the last few joined together: they become one bubble.
 * Anything else is a new thing the caller said and stays as it is.
 *
 * Shared by AvatarPanelV2.js and CobrowseWidget.js.
 */

const plain = (text) => (text || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

/**
 * @param list    captions, oldest first: { speaker, text, key?, ... }
 * @param index   the caller bubble just added or updated
 * @param opts.start   first index the fold may reach back to (a resume boundary)
 * @param opts.speaker the caller's speaker value (default 'user')
 * @returns the same list when nothing folds, otherwise a new list
 */
export const foldCallerTurn = (list, index, { start = 0, speaker = 'user' } = {}) => {
  const added = list[index];
  if (!added || added.speaker !== speaker) return list;
  const words = plain(added.text);
  if (!words) return list;

  let first = index;
  while (first - 1 >= start && list[first - 1].speaker === speaker) first -= 1;
  if (first === index) return list;

  // The same words again
  for (let i = index - 1; i >= first; i -= 1) {
    if (plain(list[i].text) === words) {
      const next = list.filter((_, n) => n !== index);
      const held = next[i];
      next[i] = {
        ...held,
        // Later chunks of a streamed copy update by key: send them here
        ...(added.key ? { key: added.key } : {}),
        text: added.text.length > held.text.length ? added.text : held.text,
        ...(added.updatedAt ? { updatedAt: added.updatedAt } : {}),
      };
      return next;
    }
  }

  // The whole turn after its parts: replace the parts with it
  for (let from = index - 2; from >= first; from -= 1) {
    const joined = plain(list.slice(from, index).map((c) => c.text).join(' '));
    if (joined === words) {
      // Keeps when the turn began
      const merged = { ...list[from], ...added, ...(list[from].at ? { at: list[from].at } : {}) };
      return [...list.slice(0, from), merged, ...list.slice(index + 1)];
    }
  }
  return list;
};

export default foldCallerTurn;
